import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Trash2,
  Plus,
  Minus,
  Tag,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { useCart } from "@/lib/cart";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, setQty, subtotal } = useCart();

  const [settings, setSettings] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);

  /* =========================================================
     LOAD SETTINGS
  ========================================================= */

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setSettings(r.data))
      .catch(() => {});
  }, []);

  /* =========================================================
     PRICING
  ========================================================= */

  const couponDiscount = Number(coupon?.discount || 0);

  const discount = Math.min(
    couponDiscount,
    Number(subtotal)
  );

  const gstPct = settings?.gst_percent ?? 5;

  const freeDeliveryAbove =
    settings?.free_delivery_above ?? 499;

  const deliveryCharge =
    subtotal >= freeDeliveryAbove || subtotal === 0
      ? 0
      : settings?.delivery_charge ?? 40;

  const afterDiscount = Math.max(
    0,
    Number(subtotal) - discount
  );

  const gst = Math.round(
    (afterDiscount * gstPct) / 100
  );

  const total = Math.max(
    0,
    afterDiscount + gst + deliveryCharge
  );

  /* =========================================================
     APPLY COUPON
  ========================================================= */

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();

    if (!code) {
      toast.error("Enter a coupon code");
      return;
    }

    try {
      const r = await api.post("/coupons/validate", {
        code,
        subtotal: Number(subtotal),
      });

      const discountAmount = Math.min(
        Number(r.data.discount || 0),
        Number(subtotal)
      );

      setCoupon({
        ...r.data,
        discount: discountAmount,
      });

      setCouponCode(code);

      toast.success(
        `Coupon applied — save ₹${discountAmount}`
      );
    } catch (e) {
      setCoupon(null);

      toast.error(
        e?.response?.data?.detail ||
          "Invalid coupon"
      );
    }
  };

  /* =========================================================
     REMOVE COUPON
  ========================================================= */

  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode("");

    toast.success("Coupon removed");
  };

  /* =========================================================
     SAVE CART PRICING FOR CHECKOUT
  ========================================================= */

  useEffect(() => {
    const pricing = {
      subtotal: Number(subtotal),

      discount: Number(discount),

      coupon_code:
        coupon?.coupon?.code ||
        coupon?.code ||
        null,

      coupon_discount: Number(discount),

      gst: Number(gst),

      gst_percent: Number(gstPct),

      delivery_charge:
        Number(deliveryCharge),

      total: Number(total),
    };

    sessionStorage.setItem(
      "icp_cart_pricing",
      JSON.stringify(pricing)
    );
  }, [
    subtotal,
    discount,
    coupon,
    gst,
    gstPct,
    deliveryCharge,
    total,
  ]);

  /* =========================================================
     EMPTY CART
  ========================================================= */

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">

        <h1 className="font-heading text-5xl mb-4">
          Your cart is empty
        </h1>

        <p className="text-[#E6F4F2] mb-8">
          Explore our menu and add some sweetness.
        </p>

        <Link
          to="/menu"
          data-testid="cart-browse-menu"
          className="btn-primary"
        >
          Browse Menu
          <ArrowRight className="w-4 h-4" />
        </Link>

      </div>
    );
  }

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-12 py-8 md:py-12">

      <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl mb-10">
        Your Cart
      </h1>

      <div className="grid lg:grid-cols-3 gap-8">

        {/* ===================================================
            CART ITEMS
        =================================================== */}

        <div className="lg:col-span-2 space-y-4">

          {items.map((it) => (
            <motion.div
              key={it.cartId}
              initial={{
                opacity: 0,
                y: 10,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              className="glass rounded-3xl p-4 flex gap-4"
              data-testid={`cart-item-${it.cartId}`}
            >

              <img
                src={it.image}
                alt={it.name}
                className="w-24 h-24 rounded-2xl object-cover"
              />

              <div className="flex-1 min-w-0">

                <div className="font-heading text-xl">
                  {it.name}
                </div>

                {it.options?.size && (
                  <div className="text-xs text-[#B7D8D5]">
                    Size: {it.options.size} ·{" "}
                    {it.options.cone}
                  </div>
                )}

                {it.options?.flavours && (
                  <div className="text-xs text-[#B7D8D5] mt-1 line-clamp-1">
                    Flavours:{" "}
                    {it.options.flavours.join(", ")}
                  </div>
                )}

                {it.options?.addons?.length > 0 && (
                  <div className="text-xs text-[#B7D8D5] line-clamp-1">
                    Add-ons:{" "}
                    {it.options.addons.join(", ")}
                  </div>
                )}

                <div className="flex items-center gap-3 mt-3">

                  <button
                    data-testid={`qty-dec-${it.cartId}`}
                    onClick={() =>
                      setQty(
                        it.cartId,
                        it.quantity - 1
                      )
                    }
                    className="w-8 h-8 rounded-full border border-[rgba(184,225,74,0.35)] bg-white text-[#1F5F5C] flex items-center justify-center"
                  >
                    <Minus className="w-3 h-3" />
                  </button>

                  <span
                    data-testid={`qty-${it.cartId}`}
                    className="font-medium w-6 text-center"
                  >
                    {it.quantity}
                  </span>

                  <button
                    data-testid={`qty-inc-${it.cartId}`}
                    onClick={() =>
                      setQty(
                        it.cartId,
                        it.quantity + 1
                      )
                    }
                    className="w-8 h-8 rounded-full border border-[rgba(184,225,74,0.35)] bg-white text-[#1F5F5C] flex items-center justify-center"
                  >
                    <Plus className="w-3 h-3" />
                  </button>

                  <button
                    data-testid={`remove-${it.cartId}`}
                    onClick={() =>
                      remove(it.cartId)
                    }
                    className="ml-auto text-[#B7D8D5] hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                </div>

              </div>

              <div className="text-right">

                <div className="font-semibold text-[#B8E14A] text-lg">
                  ₹
                  {Number(
                    it.price * it.quantity
                  ).toFixed(0)}
                </div>

              </div>

            </motion.div>
          ))}

        </div>

        {/* ===================================================
            ORDER SUMMARY
        =================================================== */}

        <div className="lg:col-span-1">

          <div className="glass rounded-3xl p-6 lg:sticky lg:top-28">

            <h3 className="font-heading text-2xl mb-4">
              Order Summary
            </h3>

            {/* =================================================
                COUPON
            ================================================= */}

            <div className="mb-5">

              <div className="text-sm font-semibold mb-2">
                Have a coupon?
              </div>

              <div className="flex gap-2">

                <div className="flex-1 flex items-center gap-2 border border-[rgba(184,225,74,0.35)] rounded-full px-4 py-2">

                  <Tag className="w-4 h-4 text-[#B7D8D5]" />

                  <input
                    data-testid="coupon-input"
                    placeholder="Coupon code"
                    value={couponCode}
                    disabled={!!coupon}
                    onChange={(e) =>
                      setCouponCode(
                        e.target.value.toUpperCase()
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        applyCoupon();
                      }
                    }}
                    className="flex-1 bg-transparent outline-none text-sm disabled:opacity-50"
                  />

                </div>

                {!coupon ? (
                  <button
                    data-testid="apply-coupon"
                    onClick={applyCoupon}
                    className="btn-outline !py-2 !px-4 text-sm"
                  >
                    Apply
                  </button>
                ) : (
                  <button
                    onClick={removeCoupon}
                    className="text-xs px-4 rounded-full bg-red-100 text-red-600 font-medium"
                  >
                    Remove
                  </button>
                )}

              </div>

            </div>

            {/* =================================================
                ACTIVE COUPON
            ================================================= */}

            {coupon && (
              <div className="mb-5 rounded-2xl bg-green-50 border border-green-200 p-4">

                <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">

                  <CheckCircle2 className="w-4 h-4" />

                  <span>
                    {coupon?.coupon?.code ||
                      coupon?.code ||
                      couponCode}
                  </span>

                </div>

                {(
                  coupon?.coupon?.description ||
                  coupon?.description
                ) && (
                  <div className="text-xs text-green-700 mt-1">
                    {coupon?.coupon?.description ||
                      coupon?.description}
                  </div>
                )}

                <div className="text-xs text-green-700 font-semibold mt-1">
                  You save ₹{discount}
                </div>

              </div>
            )}

            {/* =================================================
                PRICE BREAKDOWN
            ================================================= */}

            <div className="space-y-2 text-sm border-t border-[rgba(184,225,74,0.35)] pt-4">

              <Row
                label="Subtotal"
                value={`₹${Number(subtotal).toFixed(0)}`}
              />

              {discount > 0 && (
                <Row
                  label="Coupon Discount"
                  value={`- ₹${Number(discount).toFixed(0)}`}
                  accent
                />
              )}

              <Row
                label={`GST (${gstPct}%)`}
                value={`₹${gst}`}
              />

              <Row
                label="Delivery"
                value={
                  deliveryCharge === 0
                    ? "FREE"
                    : `₹${deliveryCharge}`
                }
              />

            </div>

            {/* =================================================
                FREE DELIVERY
            ================================================= */}

            {deliveryCharge === 0 &&
              subtotal >= freeDeliveryAbove && (
                <div className="mt-3 rounded-xl bg-green-50 border border-green-200 p-3 text-xs text-green-700 font-medium">

                  🎉 Free delivery unlocked!

                  <div className="font-normal mt-1">
                    Your order is above ₹
                    {freeDeliveryAbove}.
                  </div>

                </div>
              )}

            {/* =================================================
                TOTAL
            ================================================= */}

            <div className="border-t border-[rgba(184,225,74,0.35)] mt-3 pt-3 flex items-center justify-between">

              <span className="uppercase text-xs tracking-widest text-[#B7D8D5]">
                Grand Total
              </span>

              <span
                className="font-heading text-3xl text-[#B8E14A]"
                data-testid="grand-total"
              >
                ₹{total}
              </span>

            </div>

            {/* =================================================
                CHECKOUT
            ================================================= */}

            <Link
              to="/checkout"
              data-testid="checkout-btn"
              className="btn-primary w-full justify-center mt-4"
            >
              Proceed to Checkout
              <ArrowRight className="w-4 h-4" />
            </Link>

            <div className="text-xs text-[#B7D8D5] mt-3 text-center">
              Estimated delivery in 30–45 minutes
            </div>

          </div>

        </div>

      </div>

    </div>
  );
}

/* =========================================================
   ROW
========================================================= */

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">

      <span className="text-[#E6F4F2]">
        {label}
      </span>

      <span
        className={
          accent
            ? "text-green-700 font-medium"
            : "font-medium"
        }
      >
        {value}
      </span>

    </div>
  );
}